import type { WorkspaceMemberRole } from '@modules/projects/project-members.interface'
import { InviteEmailConflictError } from '@modules/settings/utils/InviteEmailConflictError'
import { InviteMemberFlowError } from '@modules/settings/utils/InviteMemberFlowError'
import { mapInviteConflictErrorToMessage } from '@modules/settings/utils/mapInviteConflictErrorToMessage'
import type { InviteMemberCallbacks } from '@modules/settings/workspace/stores/workspace.type'
import { TOAST_TEST_IDS } from '@stores/toast/toast.const'

export type InviteConflictMessages = {
  alreadyInvited: string
  alreadyMember: string
}

export type InviteToastConfig = {
  errorTitle: string
  informationalDescription?: string
  successDescription: string
  successTitle: string
  testId?: string
}

type InviteFn = (email: string, role: WorkspaceMemberRole, callbacks?: InviteMemberCallbacks) => Promise<void>

type ShowToast = (params: { description: string; testId?: string; title: string; variant: 'error' | 'success' }) => void

const rejectInviteFlowError = (
  reject: (reason: InviteMemberFlowError) => void,
  error: string | InviteEmailConflictError | InviteMemberFlowError,
  description: string
) => {
  if (error instanceof InviteMemberFlowError) {
    reject(error)
    return
  }

  reject(InviteMemberFlowError.stayOpen(description))
}

export function runInviteMemberWithToasts(
  inviteFn: InviteFn,
  email: string,
  role: WorkspaceMemberRole,
  options: {
    conflictMessages: InviteConflictMessages
    showToast: ShowToast
    toast: InviteToastConfig
  }
): Promise<void> {
  const { conflictMessages, showToast, toast } = options

  return new Promise((resolve, reject) => {
    void inviteFn(email, role, {
      onSuccess: () => {
        showToast({
          variant: 'success',
          title: toast.successTitle,
          description: toast.successDescription,
          testId: toast.testId ?? TOAST_TEST_IDS.inviteSuccess,
        })
        resolve()
      },
      onInformational: (message) => {
        showToast({
          variant: 'success',
          title: toast.successTitle,
          description: message || toast.informationalDescription || toast.successDescription,
        })
        reject(InviteMemberFlowError.stayOpen(message))
      },
      onError: (error) => {
        const conflictDescription = mapInviteConflictErrorToMessage(error, conflictMessages)
        const description =
          conflictDescription ??
          (error instanceof InviteMemberFlowError ? error.message : typeof error === 'string' ? error : error.message)

        showToast({
          variant: 'error',
          title: toast.errorTitle,
          description,
        })
        rejectInviteFlowError(reject, error, description)
      },
    })
  })
}
